import { RadioButtons } from '@dapp/multi-lib/sdaoui/RadioButtons';
import { VaultType } from 'structure/pv2/dynavault/types';
import { ViewTypeSwitch } from 'structure/pv2/dynavault/components/ViewTypeSwitch';
import { useVaultListContext } from 'structure/pv2/dynavault/context/VaultListContext';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import { cx } from 'class-variance-authority';
import { Icon } from '@dapp/multi-lib/sdaoui/Icon';
import { Body3 } from 'components/Typography';
import useIsMobile from '@dapp/multi-lib/ui/shared-hooks/useIsMobile';

export const VaultListControls = () => {
  const { vaultType, setVaultType, viewType } = useVaultListContext();

  const isMobile = useIsMobile();

  return (
    <div className='lg:mt-15 mt-9 flex flex-row flex-wrap gap-4'>
      <div className='grid w-full grid-cols-2 gap-4 md:grid-cols-3'>
        <div className='w-fit'>
          <RadioButtons
            value={vaultType}
            size='small'
            className='px-2 md:px-3'
            onChange={(value: VaultType) => setVaultType(value)}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Public', value: 'public' },
              { label: 'My Vaults', value: 'user' },
            ]}
          />
        </div>

        <div
          className={cx(
            'col-span-2 row-start-2 md:col-span-1 md:col-start-auto md:row-auto',
            {
              invisible: !isMobile && viewType === 'table',
              visible: isMobile,
            }
          )}
        >
          <SortControls />
        </div>

        <div className='col-start-2 row-start-1 ml-auto w-fit md:col-start-auto md:row-auto'>
          <ViewTypeSwitch />
        </div>
      </div>
    </div>
  );
};

interface SortButtonProps {
  sortDirection: 'asc' | 'desc' | 'default';
  onClick: () => void;
  children: React.ReactNode;
}

const SortButton = (props: SortButtonProps) => {
  const { sortDirection, onClick, children } = props;

  const iconClasses = cx('text-inherit', {
    'rotate-180': sortDirection === 'desc',
    'rotate-0': sortDirection === 'asc',
    hidden: sortDirection === 'default',
  });

  const style = sortDirection !== 'default' ? 'neutral' : 'outline';

  return (
    <Button
      size='small'
      style={style}
      className='hover:bg-[linear-gradient(103deg,_#363636_0%,_#1A1A1A_100%)] hover:text-white'
      onClick={onClick}
    >
      <div className='flex cursor-pointer flex-row items-center gap-1'>
        <Icon
          icon='arrow_upward'
          className={iconClasses}
          color={sortDirection === 'default' ? '#B5B5B5' : '#F0F0F0'}
        />
        {children}
      </div>
    </Button>
  );
};

const SortControls = () => {
  const { getSortDirection, changeSortDirection } = useVaultListContext();

  return (
    <div className='flex flex-row items-center gap-2'>
      <Body3 className='text-nowrap'>Sort by:</Body3>
      <SortButton
        sortDirection={getSortDirection('tvl')}
        onClick={() => changeSortDirection('tvl')}
      >
        TVL
      </SortButton>
      <SortButton
        sortDirection={getSortDirection('apy')}
        onClick={() => changeSortDirection('apy')}
      >
        APY
      </SortButton>
      <SortButton
        sortDirection={getSortDirection('price')}
        onClick={() => changeSortDirection('price')}
      >
        Price
      </SortButton>
      <SortButton
        sortDirection={getSortDirection('position')}
        onClick={() => changeSortDirection('position')}
      >
        Position
      </SortButton>
    </div>
  );
};
